// Phone check-in data layer (client). Wraps Firestore reads and writes so the
// React tool never touches raw collections. Collection shapes:
//
//   students/{studentId}                         // 6-digit ID = card barcode
//     name, station, slot, active, noPhone
//
//   phoneCheckIn/{YYYY-MM-DD}                     // day doc, mirrors attendance
//     date, recordCount, source, lastUpdatedAt
//   phoneCheckIn/{YYYY-MM-DD}/records/{studentId}
//     studentId, status, station, slot, operator, note, early,
//     checkInAt, checkOutAt, updatedAt
//
//   dailySchedule/{YYYY-MM-DD}                    // read-only here
//
// DB status values: "pending" | "turnedIn" | "returned" | "noPhone".
// The React UI uses "pending" | "in" | "returned" | "nophone". The two maps
// below translate, so the existing component keeps its own vocabulary.

import { db } from "../config/firebase";
import {
  collection, doc, getDoc, getDocs, onSnapshot,
  serverTimestamp, writeBatch, setDoc,
} from "firebase/firestore";

export const TZ = "Asia/Amman";

// The cubby field is the single source of truth for a student's location and
// slot. Prefix -> station id; the number after the dash is the slot.
export const CUBBY_STATION = {
  SE: "sulafat-east", SW: "sulafat-west", AL: "alnilam", ME: "meissa",
  MU: "murzim", G: "nihal", AT: "atair",
  G9: "grade-9", G10: "grade-10", G11: "grade-11", G12: "grade-12",
};
const CUBBY_PREFIXES = Object.keys(CUBBY_STATION).sort((a, b) => b.length - a.length);

// "G11-42" -> { key:"G11", station:"grade-11", slot:42 }. Splits on the first
// dash so grade prefixes (which contain digits) work. Returns null if unknown.
export function parseCubby(cubby) {
  if (cubby == null || String(cubby).trim() === "") return null;
  const s = String(cubby).trim().toUpperCase();
  let key, numStr;
  const dash = s.indexOf("-");
  if (dash > 0) { key = s.slice(0, dash); numStr = s.slice(dash + 1); }
  else { key = CUBBY_PREFIXES.find((k) => s.startsWith(k)); numStr = key ? s.slice(key.length) : ""; }
  const raw = (numStr || "").trim();
  if (!CUBBY_STATION[key] || raw === "") return null;
  // Slot may be a room code (e.g. "G06") for locations renamed to room numbers,
  // so keep it as a string unless it is purely numeric.
  const slot = /^\d+$/.test(raw) ? parseInt(raw, 10) : raw;
  return { key, station: CUBBY_STATION[key], slot };
}


// Current school-day key in Amman local time. The day rolls over at midnight
// Amman, and the 4 AM seed fills that day before stations open.
export function dayKey(d = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

const dbToUi = (s) => (s === "turnedIn" ? "in" : s === "noPhone" ? "nophone" : s || "pending");

function recRef(date, studentId) {
  return doc(db, "phoneCheckIn", date, "records", String(studentId));
}

// Live roster of active students. Doc id is the student ID (= barcode).
export function subscribeRoster(cb) {
  // A student counts as active unless explicitly active === false, so a plain
  // Veracross upload (no active field) still loads.
  return onSnapshot(collection(db, "students"), (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.active !== false))
  );
}

// Live day records, returned as a map keyed by student ID with UI statuses.
export function subscribeDay(date, cb) {
  const col = collection(db, "phoneCheckIn", date, "records");
  return onSnapshot(col, (snap) => {
    const map = {};
    snap.forEach((d) => {
      const r = d.data();
      map[d.id] = { ...r, status: dbToUi(r.status) };
    });
    cb(map);
  });
}

export async function checkIn(date, studentId, { station, operator } = {}) {
  await setDoc(recRef(date, studentId), {
    studentId: String(studentId),
    status: "turnedIn",
    station: station || null,
    operator: operator || null,
    checkInAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function checkOut(date, studentId, { station, operator, note, early } = {}) {
  await setDoc(recRef(date, studentId), {
    studentId: String(studentId),
    status: "returned",
    station: station || null,
    operator: operator || null,
    note: note || null,
    early: !!early,
    checkOutAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function setNoPhone(date, studentId, on) {
  await setDoc(recRef(date, studentId), {
    studentId: String(studentId),
    status: on ? "noPhone" : "pending",
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// Master checkout: every record currently turned in becomes returned, tagged as
// a system-wide checkout. `records` is the map from subscribeDay. Returns how
// many were released. Batches stay under Firestore's 500-write limit.
export async function masterCheckout(date, records, { operator } = {}) {
  const ids = Object.keys(records).filter((id) => records[id].status === "in");
  for (let i = 0; i < ids.length; i += 450) {
    const batch = writeBatch(db);
    ids.slice(i, i + 450).forEach((id) => {
      batch.set(recRef(date, id), {
        status: "returned",
        operator: operator || "system",
        note: "system-wide master checkout",
        checkOutAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    });
    await batch.commit();
  }
  return ids.length;
}

// Read-only daily schedule for a date (from importDailySchedule.js).
export async function getSchedule(date) {
  const snap = await getDoc(doc(db, "dailySchedule", date));
  return snap.exists() ? snap.data() : null;
}

// Undo: put a record back to pending and clear its timestamps.
export async function resetRecord(date, studentId) {
  await setDoc(recRef(date, studentId), {
    studentId: String(studentId),
    status: "pending",
    checkInAt: null,
    checkOutAt: null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// Commit a manually reconciled location (dean's cubby grid). rows is a list of
// { id, station, slot, turnedIn }. Unclicked cubbies are turnedIn; clicked
// (reported missing) stay not-turned-in. Tagged source "dean-manual".
export async function commitManualLocation(date, rows, { operator } = {}) {
  for (let i = 0; i < rows.length; i += 450) {
    const batch = writeBatch(db);
    rows.slice(i, i + 450).forEach((r) => {
      batch.set(recRef(date, r.id), {
        studentId: String(r.id),
        status: r.turnedIn ? "turnedIn" : "missing",
        station: r.station || null,
        slot: r.slot ?? null,
        operator: operator || "dean-manual",
        source: "dean-manual",
        note: r.turnedIn ? null : "reported missing",
        checkInAt: r.turnedIn ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        reconciledAt: serverTimestamp(),
      }, { merge: true });
    });
    await batch.commit();
  }
  return rows.length;
}

// History for the chronic report. Given a list of date keys, returns per day
// (with any records) each student's checkedIn / retrieved flags from the
// end-of-day status. Days with no records are skipped.
export async function fetchHistory(dateKeys) {
  const out = [];
  for (const date of dateKeys) {
    const snap = await getDocs(collection(db, "phoneCheckIn", date, "records"));
    if (snap.empty) continue;
    const entries = snap.docs.map((d) => {
      const st = d.data().status;
      return {
        id: d.id,
        checkedIn: st === "turnedIn" || st === "returned",
        retrieved: st === "returned",
      };
    });
    out.push({ date, entries });
  }
  return out;
}